import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import {
  CountryUS,
  FlowTypes,
  IRATrustTypesMapper,
  IReceipt,
  ReceiptRow,
  urlMapper,
  YesNoEmpty,
} from '@interfaces/models';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import {
  PersonalAddressData,
  PersonalAddressGetData,
} from '@interfaces/models/open-account-mfes/personal-address-mfe.model';
import { ClearData } from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { TrustNumber } from '@shared/models/trust';
import { AccountTriageContent } from '@shared/services/content/content-jsons/account-triage-content';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { TransactionState } from '@store/reducers';
import { FinancialDetailsState } from '@store/reducers/income-net-worth/income-net-worth.reducer';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { MfePersonalAddressState } from '@store/reducers/personal-info-address/personal-info-address.reducers';
import { getIsClient, TriageState } from '@store/reducers/triage/triage.reducer';
import {
  buildNewAccountProfile,
  splitClientId,
} from '@store/selectors/receipt/new-account/new-account.cashier';
import { buildAccountApprovalLetterRow } from '@store/selectors/receipt/receipt.helpers';
import * as dayjs from 'dayjs';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

interface CompanyRow {
  label: string;
  values: string[];
  evidenceID: string;
}

interface Profile {
  header: string;
  editable: boolean;
  editRoute: string;
  sectionEvidenceID: string;
  rows: ReceiptRow[];
}

export function buildEmploymentMultiInheritanceReceipt(
  state: Partial<VgEmploymentState> | undefined,
  isNewAccountTransactionExist: boolean,
  triageState: TriageState,
  letterUploadState: ILetterUploadState,
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.employment,
  wldLetterDocuments: UploadedFileEntry[] = [],
): IReceipt | null {
  if (!isNewAccountTransactionExist) {
    return null;
  }

  const empStatus = state?.employmentStatus;
  const empAssociations = state?.associations;
  const {
    street = '',
    city = '',
    state: addressState = '',
    zipCode = '',
  } = empStatus?.streetAddress || {};

  const statusCode = empStatus?.employmentStatus || '';
  let statusCodeText: string;
  const businessAddressLine1 = street;
  const businessAddressLine2 = [city, [addressState, zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const employer = empStatus?.employer || '';
  const occupation = empStatus?.occupation || '';
  const employmentStatusRows = [
    {
      label: content?.employerLabel || 'Employer',
      values: [empStatus?.employmentStatus === 'VE' ? 'Vanguard' : employer],
      evidenceID: 'employmentEmployer',
    },
    {
      label: content?.occupationLabel || 'Occupation',
      values: [occupation],
      evidenceID: 'employmentOccupation',
    },
    {
      label: content?.businessStreetAddressLabel || 'Business street address',
      values: [businessAddressLine1, businessAddressLine2],
      evidenceID: 'employmentAddress',
    },
  ];

  const selfEmployedRows = [
    {
      label: content?.companyNameLabel || 'Company name',
      values: [employer],
      evidenceID: 'employmentEmployer',
    },
    {
      label: content?.natureOfBusinessLabel || 'Nature of business',
      values: [occupation],
      evidenceID: 'employmentOccupation',
    },
  ];

  const associationsRows = [
    {
      label: content?.spouseIsVgEmployeeLabel || 'Spouse is a Vanguard employee',
      values: [empAssociations?.spouse ? 'Yes' : 'No'],
      evidenceID: 'employmentSpouseIsVanguardEmployee',
    },
    {
      label: content?.associatedPersonLabel || 'Associated person',
      values: [empAssociations?.associated ? 'Yes' : 'No'],
      evidenceID: 'employmentAssociatedPerson',
    },
  ];

  const approvalLetterRow = buildAccountApprovalLetterRow(letterUploadState, wldLetterDocuments);
  if (approvalLetterRow) {
    associationsRows.push(approvalLetterRow);
  }

  associationsRows.push({
    label: content?.controlPersonLabel || 'Control person',
    values: [empAssociations?.control ? 'Yes' : 'No'],
    evidenceID: 'employmentControlPerson',
  });

  let controlCompaniesRows: CompanyRow[] = [];
  if (empAssociations?.control) {
    const companies: string[] = [];
    const tickers = empAssociations.controlCompanyTicker ?? [];
    const names = empAssociations.controlCompanyName ?? [];
    const length = Math.min(tickers.length, names.length);
    for (let ind = 0; ind < length; ind++) {
      companies.push(`${tickers[ind]}-${names[ind]}`);
    }
    controlCompaniesRows = [
      {
        label: 'Control companies',
        values: [...companies],
        evidenceID: 'controlPersonCompanies',
      },
    ];
  }

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: content?.employmentLabel || 'Employment',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/employment`,
    sectionEvidenceID: 'employment',
    rows: [
      {
        label: 'Status',
        values: [],
        evidenceID: 'employmentStatus',
      },
    ],
  };

  switch (statusCode) {
    case 'E':
      statusCodeText = 'Employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...employmentStatusRows, ...associationsRows, ...controlCompaniesRows);
      break;
    case 'VE':
      statusCodeText = 'Employed by Vanguard';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(employmentStatusRows[1], ...associationsRows, ...controlCompaniesRows);
      break;
    case 'SE':
      statusCodeText = 'Self-employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(
        ...selfEmployedRows,
        employmentStatusRows[2],
        ...associationsRows,
        ...controlCompaniesRows,
      );
      break;
    case 'R':
      statusCodeText = 'Retired';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...associationsRows, ...controlCompaniesRows);
      break;
    case 'U':
      statusCodeText = 'Not employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...associationsRows, ...controlCompaniesRows);
      break;
    default:
      break;
  }
  return receipt;
}

export function buildFinancialProfileMultiInheritanceReceipt(
  financial: FinancialDetailsState,
  isNewAccountTransactionExist: boolean,
  editable: boolean,
  content?: typeof ReviewContent.receiptContent.financialProfile,
) {
  if (!isNewAccountTransactionExist) {
    return null;
  }

  const flow = urlMapper[FlowTypes.MULTI_INHERITANCE];

  const moneyOptions = {
    G: 'Less than $20,000',
    H: '$20,000 - $49,999',
    I: '$50,000 - $74,999',
    J: '$75,000 - $99,999',
    K: '$100,000 - $499,999',
    L: '$500,000 - $999,999',
    M: '$1,000,000 or above',
  };

  const financialProfileReceipt: IReceipt = {
    header: content?.financialProfileLabel || 'Financial profile',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/income-net-worth`,
    sectionEvidenceID: 'financialProfile',
    rows: [],
  };

  if (financial.annualIncome) {
    financialProfileReceipt.rows.push(
      {
        label: content?.incomeLabel || 'Income',
        values: [moneyOptions[financial.annualIncome as keyof typeof moneyOptions]],
        evidenceID: 'income',
      },
      {
        label: content?.netWorthLabel || 'Net worth',
        values: [moneyOptions[financial.liquidNetWorth as keyof typeof moneyOptions]],
        evidenceID: 'networth',
      },
    );
  }
  return financialProfileReceipt;
}

export function buildDefaultBeneficiaryMultiInheritanceReceipt(
  isNewAccountTransactionExist: boolean,
  isIraMultiInheritanceFlow: boolean,
  trustTransactions: TransactionState[],
  transactionsCount: number,
): IReceipt | null {
  const onlyTrustTransactions = trustTransactions.length === transactionsCount;
  const isShownForMultiInheritanceFlow =
    isNewAccountTransactionExist && isIraMultiInheritanceFlow && !onlyTrustTransactions;

  const receipt: IReceipt = {
    header: 'Beneficiaries',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'defaultBeneficiaries',
    rows: [
      {
        label: '',
        values: [
          `Assets will pass to your surviving spouse or your estate. This is the current default according to Vanguard\'s IRA Custodial Account Agreement.`,
        ],
        modal: ReceiptModal.BENEFICIARIES,
        evidenceID: 'defaultBene',
      },
    ],
  };

  return isShownForMultiInheritanceFlow ? receipt : null;
}

export function buildEstateMultiInheritanceReceipt(
  transaction: TransactionState,
  editable: boolean,
  content?: typeof AccountTriageContent.estate,
  hideWithholding: boolean = false,
): IReceipt | null {
  const { estate, currentTransactionId } = transaction;

  if (
    !estate ||
    !estate.estateName ||
    !estate.tin_private ||
    transaction?.accountTriage?.isDecedentSpouse === YesNoEmpty.YES
  ) {
    return null;
  }

  const receipt: IReceipt = {
    header: content?.header || 'Estate information',
    editable: editable,
    editRoute: 'secure/multi-inheritance/estate',
    sectionEvidenceID: 'estate',
    editTransferId: currentTransactionId,
    rows: [
      {
        label: content?.estateName || 'Estate name',
        values: [estate.estateName],
        evidenceID: 'estateName',
      },
      {
        label: content?.estateTin || 'Estate tax identification number',
        values: [editable ? estate.tin_private : '**-***' + splitClientId(estate.tin_private)],
        evidenceID: 'estateTin',
      },
      {
        label: content?.federalTaxClassification || 'Federal tax classification',
        values: ['Estate'],
        evidenceID: 'federalTaxClassification',
      },
      {
        label: content?.estateCountry || 'Country where estate was established',
        values: [estate.countryQuestion ?? ''],
        evidenceID: 'estateCountry',
      },
    ],
  };

  if (estate.taxResidencyQuestion) {
    receipt.rows.push({
      label: content?.estateTaxResidency || 'Tax residency',
      values: [estate.taxResidencyQuestion],
      evidenceID: 'estateTaxResidency',
    });
  }

  if (!hideWithholding && estate.withholdingQuestion) {
    receipt.rows.push({
      label: content?.estateWithholding || 'Are you subject to backup withholding?',
      values: [estate.withholdingQuestion],
      evidenceID: 'estateWithholding',
    });
  }

  return receipt;
}

export function buildTrustInfoMultiInheritanceReceipt(
  transaction: TransactionState,
  editable: boolean,
  hideWithholding: boolean = false,
): IReceipt | null {
  const { trust, trustTaxInfo, accountTriage, currentTransactionId } = transaction;

  if (!trust || !trustTaxInfo) {
    return null;
  }

  const rows: ReceiptRow[] = [];
  const numberQuestion = trustTaxInfo.numberQuestion;

  rows.push(
    {
      label: 'Trust type',
      values: [accountTriage.typeOfTrust ? IRATrustTypesMapper[accountTriage.typeOfTrust] : ''],
      evidenceID: 'trustType',
    },
    {
      label: 'Full legal trust name',
      values: [trust.trustName ?? ''],
      evidenceID: 'trustName',
    },
  );

  if (trust.trustDate) {
    rows.push({
      label: 'Trust date',
      values: [dayjs(trust.trustDate).format('MM/DD/YYYY')],
      evidenceID: 'trustDate',
    });
  }

  const trustAddress = trust.trustAddress
    ? ['Same as trustee']
    : [trust.address ?? '', `${trust.city ?? ''}, ${trust.state ?? ''} ${trust.zipCode ?? ''}`];

  const mailingAddress = trust.mailingAddress
    ? ['Same as trust']
    : [
        trust.mailAddress ?? '',
        `${trust.mailCity ?? ''}, ${trust.mailState ?? ''} ${trust.mailZipCode ?? ''}`,
      ];

  rows.push(
    {
      label: 'Country where the trust was established',
      values: [trust.countryQuestion ?? ''],
      evidenceID: 'establishmentCountry',
    },
    {
      label: 'Tax residency',
      values: [trust.taxResidencyQuestion ?? ''],
      evidenceID: 'taxResidency',
    },
    {
      label: 'Trust address',
      values: trustAddress,
      evidenceID: 'trustAddress',
    },
    {
      label: 'Trust mailing address',
      values: mailingAddress,
      evidenceID: 'trustMailingAddress',
    },
    {
      label: 'Does the trust report under an EIN or Social Security number?',
      values: [numberQuestion ?? ''],
      evidenceID: 'numberQuestion',
    },
    {
      label: 'Federal tax classification',
      values: [numberQuestion === TrustNumber.EIN ? 'Trust' : 'Individual'],
      evidenceID: 'trustTaxClassification',
    },
  );

  if (numberQuestion === TrustNumber.EIN) {
    rows.push({
      label: 'Trust identification number',
      values: editable
        ? [(trustTaxInfo.ein ?? '') + ' (EIN)']
        : ['**-***' + splitClientId(trustTaxInfo.ein ?? '') + ' (EIN)'],
      evidenceID: 'trustTaxIdentificationNumber',
    });
  }

  if (numberQuestion === TrustNumber.SSN) {
    rows.push(
      {
        label: 'Grantor',
        values: [trustTaxInfo.grantorName ?? ''],
        evidenceID: 'trustGrantorName',
      },
      {
        label: 'Grantor Social Security number',
        values: editable
          ? [trustTaxInfo.ssn ?? '']
          : ['***-**-' + splitClientId(trustTaxInfo.ssn ?? '')],
        evidenceID: 'trustGrantorSSN',
      },
      {
        label: 'Grantor citizenship',
        values: [trust.grantorQuestion === CountryUS.US ? 'U.S. citizen' : 'Other'],
        evidenceID: 'trustGrantorCitizenship',
      },
    );
  }

  if (!hideWithholding) {
    rows.push({
      label: 'Subject to backup withholding',
      values: [trustTaxInfo.withholdingQuestion],
      evidenceID: 'backupWithholding',
    });
  }

  return {
    header: 'Trust information',
    description: null,
    editable: editable,
    sectionEvidenceID: 'trustInfo',
    editRoute: 'secure/multi-inheritance/account-triage',
    editTransferId: currentTransactionId,
    rows: rows,
  };
}

export function buildMultiInheritanceProfileReceipt(
  transaction: TransactionState,
  triageState: TriageState,
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  ssn_private: string,
  address: MfePersonalAddressState,
  phone: IPersonalPhoneState,
  clearData: ClearData,
  isNewAccountTransaction: boolean,
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.profile,
): IReceipt {
  const { accountTriage } = transaction;
  const { isOnlyExecutor, isOnlyTrustee } = accountTriage;

  const client: boolean = getIsClient(triageState);

  let header = '';

  if (isOnlyExecutor === YesNoEmpty.YES) {
    header = 'Executor information';
  } else if (isOnlyTrustee === YesNoEmpty.YES) {
    header = 'Trustee Information';
  } else {
    header = content?.profileLabel || 'Profile';
  }

  const profile: Profile = {
    header: header,
    editable: editable && client,
    editRoute: `secure/multi-inheritance/personal-info`,
    sectionEvidenceID: 'profile',
    rows: [],
  };

  buildNewAccountProfile(
    profile,
    personalInfo,
    ssn_private,
    address,
    phone,
    editable,
    client,
    false,
    isOnlyExecutor === YesNoEmpty.YES,
    FlowTypes.MULTI_INHERITANCE,
    isNewAccountTransaction,
    content,
  );

  if (clearData?.uploadedFilename) {
    profile.rows.push({
      label: 'Document for verification',
      values: [clearData.uploadedFilename],
      evidenceID: 'clearDocumentVerification',
    });
  }

  return profile;
}
